// ============================================================================
// HrGuard — route-layer access control for /hr/*.
// Allowed roles: hr, executive, superuser. Everyone else sees a brief
// "Access restricted" banner for ~1.5s, then is redirected to /account-management.
//
// RLS at the DB layer is the real enforcement (hr_read_all + superuser_all
// + tcs_employee_read_all + executive_read_all). This guard is for UX —
// to keep AMs from landing on a barely-populated HR screen with no nav back.
// ============================================================================
import { isDeskSeat } from '../../shared/role-gates';
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../shared/auth';


export function HrGuard() {
  const { profile, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Bounce non-allowed roles back to AM home after a brief "Access restricted"
  // banner. useEffect keeps the side-effect out of render.
  useEffect(() => {
    if (loading) return;
    if (!profile) return;
    if (!isDeskSeat(profile)) {
      // eslint-disable-next-line no-console
      console.warn(
        '[hr-guard] role',
        profile.role,
        'attempted /hr/*; redirecting to /account-management',
      );
      const t = setTimeout(
        () => navigate('/account-management', { replace: true }),
        1500,
      );
      return () => clearTimeout(t);
    }
  }, [profile, loading, navigate, location.pathname]);

  if (loading) {
    return (
      <div className="p-8 text-sm text-tcs-text-muted">Loading HR module…</div>
    );
  }
  if (!profile) {
    return <Navigate to="/" replace />;
  }
  if (!isDeskSeat(profile)) {
    // Brief feedback before the effect navigates away.
    return (
      <div className="p-8" role="alert" aria-live="polite">
        <div className="max-w-md rounded-md border border-sentiment-critical-text/30 bg-sentiment-critical-fill p-4 text-sm text-sentiment-critical-text">
          <div className="font-medium">Access restricted</div>
          <div className="mt-1 text-tcs-text-secondary">
            HR module requires HR, executive, or superuser role. Redirecting…
          </div>
        </div>
      </div>
    );
  }
  return <Outlet />;
}
